import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Search, Edit, Gamepad2, Plus } from "lucide-react"
import { useAllGames } from "@/graphql/hooks/useGames"
import { Skeleton } from "@/components/ui/skeleton"
import { GameEditModal } from "./GameEditModal"
import type { Game } from "@/graphql/types"

export function GamesManagement() {
  const [searchTerm, setSearchTerm] = useState("")
  const [editingGame, setEditingGame] = useState<Game | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const { games, loading, error, refetch } = useAllGames(false)

  const filteredGames =
    games?.filter((game) => game.name.toLowerCase().includes(searchTerm.toLowerCase())) || []

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            Error cargando juegos: {error.message}
            <Button onClick={() => refetch()} className="ml-4" variant="outline">
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar juegos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Juego
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gamepad2 className="h-5 w-5" />
            Juegos ({filteredGames.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Orden</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Torneos</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGames.map((game) => (
                <TableRow key={game.id}>
                  <TableCell className="font-medium flex items-center gap-2">
                    <img
                      src={game.urlImage || "/placeholder.svg"}
                      alt={game.name}
                      className="h-12 rounded object-cover"
                    />
                    {game.name}
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {game.description || "Sin descripción"}
                  </TableCell>
                  <TableCell>{game.sortOrder || "N/A"}</TableCell>
                  <TableCell>
                    <Badge variant={game.isActive ? "default" : "secondary"}>
                      {game.isActive ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell>{game.totalTournaments}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingGame(game)
                          setIsEditModalOpen(true)
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <GameEditModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        game={editingGame}
        onSuccess={refetch}
      />
    </div>
  )
}
